import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, MessageSquare } from 'lucide-react';
import { useSavList } from '@/features/savs/hooks/useSavList';
import { DataList } from '@/Components/shared/List';
import { useListState, useListSearch } from '@/hooks/list';
import { paginationToSkipTake } from '@/utils/list.utils';
import type { ColumnDef, BaseListFilters } from '@/types/list.types';
import { FormInput } from '@/Components/shared/FormInput';
import { FormSelect } from '@/Components/shared/FormSelect';
import type { SavListItem, SavListFilters } from '@/features/savs/types/sav.types';
import { SavMobileCards } from './SavMobileCards';

interface SavFilters extends BaseListFilters {
  status?: string;
  statusGroup?: string;
  virtualStatus?: string;
  declarationDateFrom?: string;
  declarationDateTo?: string;
  incidentDateFrom?: string;
  incidentDateTo?: string;
}

interface StatusCounts {
  enCours: number;
  termines: number;
  sansSuite: number;
}

interface SavListPageProps {
  onRowClick?: (sav: SavListItem) => void;
  /** Embedded mode (no outer padding/margins). Default: false */
  embedded?: boolean;
  /** Masquer le titre dans le DataList (le parent gère son propre header) */
  hideTitle?: boolean;
  /** Callback pour remonter le total au parent */
  onTotalChange?: (total: number) => void;
  /** Callback pour remonter les counts de statut au parent */
  onStatusCountsChange?: (counts: StatusCounts) => void;
  /** Filtrer par société (pour les onglets de la vue société) */
  societyId?: string;
  /** Actions custom dans le header (boutons) */
  headerActions?: React.ReactNode;
  /** Afficher le tag V1/V2 dans la colonne ID. Default: false */
  showVersionTag?: boolean;
  /** Masquer la colonne Société. Default: false */
  hideCompanyColumn?: boolean;
  /** Rendre le nom de société cliquable vers /vendors/:id. Réservé à l'admin
   *  (la route /vendors/:id n'existe pas côté pro v2 → 404). Default: false */
  linkCompanyToVendor?: boolean;
}

// Préfixe "group:" pour les statusGroup, valeur brute pour les status individuels
const SAV_STATUS_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'group:ouvert', label: 'Ouvert' },
  { value: 'group:ferme', label: 'Fermé' },
];

const SAV_GESTION_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'Déclaration', label: 'Déclaration' },
  { value: 'Recherche réparateur', label: 'Recherche réparateur' },
  { value: 'Analyse Affinicar', label: 'Analyse Affinicar' },
  { value: 'En attente signature garant', label: 'En attente signature garant' },
  { value: 'En attente signature bénéficiaire', label: 'En attente signature bénéficiaire' },
  { value: 'Refusé par le garant', label: 'Refusé par le garant' },
  { value: 'Pris en charge', label: 'Pris en charge' },
  { value: 'Sans suite', label: 'Sans suite' },
];

function formatAmount(amountCents?: number): string {
  if (amountCents == null) return '-';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amountCents / 100);
}

export function SavListPage({ onRowClick, embedded = false, hideTitle = false, onTotalChange, onStatusCountsChange, societyId, headerActions: customHeaderActions, showVersionTag = false, hideCompanyColumn = false, linkCompanyToVendor = false }: SavListPageProps) {
  const {
    filters,
    updateFilter,
    pagination,
    setPagination,
    sortConfig,
    setSortConfig,
    resetFilters,
    activeFiltersCount,
  } = useListState<SavFilters>({
    defaultLimit: 20,
    defaultSortBy: 'createdAt',
    defaultSortDirection: 'DESC',
    persistInUrl: true,
  });

  const { searchTerm, debouncedSearch, setSearchTerm } = useListSearch({
    debounceMs: 300,
    persistInUrl: true,
  });

  // Séparer group:xxx (statusGroup) des statuts individuels (status)
  const statusFilterValue = filters.status || '';
  const isGroupFilter = statusFilterValue.startsWith('group:');
  const resolvedStatusGroup = isGroupFilter ? statusFilterValue.replace('group:', '') as SavListFilters['statusGroup'] : undefined;
  const resolvedStatus = !isGroupFilter ? (statusFilterValue || undefined) : undefined;

  const apiFilters = useMemo(() => {
    const { skip, take } = paginationToSkipTake(pagination.page, pagination.limit);
    return {
      skip,
      take,
      search: debouncedSearch || undefined,
      status: resolvedStatus,
      statusGroup: resolvedStatusGroup,
      virtualStatus: filters.virtualStatus || undefined,
      declarationDateFrom: filters.declarationDateFrom || undefined,
      declarationDateTo: filters.declarationDateTo || undefined,
      incidentDateFrom: filters.incidentDateFrom || undefined,
      incidentDateTo: filters.incidentDateTo || undefined,
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
      ...(societyId ? { societyId } : {}),
    };
  }, [pagination, debouncedSearch, resolvedStatus, resolvedStatusGroup, filters.virtualStatus, filters.declarationDateFrom, filters.declarationDateTo, filters.incidentDateFrom, filters.incidentDateTo, sortConfig, societyId]);

  const { data, isLoading, error } = useSavList(apiFilters);

  // Queries légères pour les counts par groupe de statuts
  const { data: enCoursData } = useSavList({ take: 1, statusGroup: 'en_cours', ...(societyId ? { societyId } : {}) });
  const { data: terminesData } = useSavList({ take: 1, statusGroup: 'termines', ...(societyId ? { societyId } : {}) });
  const { data: sansSuiteData } = useSavList({ take: 1, statusGroup: 'sans_suite', ...(societyId ? { societyId } : {}) });

  const savList = useMemo(() => data?.data || [], [data]);
  const total = useMemo(() => data?.total || 0, [data]);

  useEffect(() => { onTotalChange?.(total); }, [total, onTotalChange]);

  useEffect(() => {
    onStatusCountsChange?.({
      enCours: enCoursData?.total ?? 0,
      termines: terminesData?.total ?? 0,
      sansSuite: sansSuiteData?.total ?? 0,
    });
  }, [enCoursData?.total, terminesData?.total, sansSuiteData?.total, onStatusCountsChange]);

  const columns: ColumnDef<SavListItem>[] = useMemo(() => [
    {
      key: 'id',
      label: 'ID',
      sortKey: 'createdAt',
      render: (sav) => (
        <div>
          <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded tw-bg-gray-100 tw-text-gray-800">
            {sav.uniqueId || `#${sav.id}`}
          </span>
          {sav.uniqueId && (
            <span className="tw-ml-1 tw-text-xs tw-text-gray-500">
              #{sav.id}
            </span>
          )}
          {showVersionTag && sav.isV2 && (
            <span className="tw-ml-1 tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-medium tw-rounded tw-bg-indigo-100 tw-text-indigo-700">
              V2
            </span>
          )}
          {sav.unreadGarantMessagesCount && sav.unreadGarantMessagesCount > 0 ? (
            <span
              title="Messages non lus"
              className="tw-ml-1 tw-inline-flex tw-items-center tw-gap-0.5 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-bold tw-rounded-full tw-bg-blue-500 tw-text-white tw-align-middle"
            >
              <MessageSquare className="tw-w-3 tw-h-3" />
              {sav.unreadGarantMessagesCount}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'companyName',
      sortKey: 'companyName',
      label: 'Société',
      render: (sav) => {
        if (!sav.companyName) return <span className="tw-text-sm tw-text-gray-400">-</span>;
        // Lien vers la fiche société réservé à l'admin : la route /vendors/:id
        // n'existe pas côté pro v2 (→ 404). Ailleurs, simple texte.
        if (!sav.societyId || !linkCompanyToVendor) {
          return <span className="tw-text-sm tw-font-medium">{sav.companyName}</span>;
        }
        return (
          <Link
            to={`/vendors/${sav.societyId}`}
            onClick={(e) => e.stopPropagation()}
            className="tw-text-sm tw-font-medium tw-text-blue-600 hover:tw-text-blue-800 hover:tw-underline"
          >
            {sav.companyName}
          </Link>
        );
      },
    },
    {
      key: 'contractLabel',
      sortKey: 'contractLabel',
      label: 'Contrat',
      render: (sav) => (
        <div>
          <span className="tw-text-sm">{sav.contractLabel || '-'}</span>
          {sav.warrantyName && (
            <div className="tw-text-xs tw-text-gray-500 tw-truncate tw-max-w-[150px]" title={sav.warrantyName}>
              {sav.warrantyName}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'beneficiary',
      sortKey: 'beneficiary',
      label: 'Bénéficiaire',
      render: (sav) => {
        const name = [sav.beneficiaryFirstName, sav.beneficiaryLastName]
          .filter(Boolean)
          .join(' ');
        return (
          <div>
            <div className="tw-text-sm tw-font-medium">{name || '-'}</div>
            {sav.beneficiaryEmail && (
              <div className="tw-text-xs tw-text-gray-500">{sav.beneficiaryEmail}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'vehicle',
      label: 'Véhicule',
      render: (sav) => {
        const info = [sav.vehicleBrand, sav.vehicleModel].filter(Boolean).join(' ');
        return (
          <div>
            <div className="tw-text-sm">{sav.vehicleRegistration || '-'}</div>
            {info && <div className="tw-text-xs tw-text-gray-500">{info}</div>}
          </div>
        );
      },
    },
    {
      key: 'status',
      sortKey: 'stateId',
      label: 'Statut',
      render: (sav) => {
        const isClosed = sav.virtualStatusLabel === 'Pris en charge' || sav.virtualStatusLabel === 'Sans suite';
        return (
          <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded ${isClosed ? 'tw-bg-gray-100 tw-text-gray-600' : 'tw-bg-green-100 tw-text-green-700'}`}>
            {isClosed ? 'Fermé' : 'Ouvert'}
          </span>
        );
      },
    },
    {
      key: 'gestion',
      label: 'État gestion',
      render: (sav) => (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-flex-wrap">
          {sav.virtualStatusLabel ? (
            <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded ${
              sav.needsGarantSignature
                ? 'tw-bg-red-100 tw-text-red-700'
                : sav.virtualStatusLabel.includes('attente')
                  ? 'tw-bg-orange-100 tw-text-orange-700'
                  : sav.virtualStatusLabel.includes('Pris en charge') || sav.virtualStatusLabel.includes('Terminé')
                    ? 'tw-bg-green-100 tw-text-green-700'
                    : 'tw-bg-affinicar-lighter tw-text-affinicar-primary'
            }`}>
              {sav.virtualStatusLabel}
            </span>
          ) : (
            <span className="tw-text-gray-400">-</span>
          )}
          {sav.needsGarantPayment && (
            <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded tw-bg-amber-100 tw-text-amber-800">
              Facture à régler
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'declarationDate',
      sortKey: 'declarationDate',
      label: 'Déclaration',
      render: (sav) =>
        sav.declarationDate ? (
          <span className="tw-text-sm tw-text-gray-500">
            {new Date(sav.declarationDate).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </span>
        ) : (
          <span className="tw-text-gray-400">-</span>
        ),
    },
    {
      key: 'incidentDate',
      sortKey: 'incidentDate',
      label: 'Date de panne',
      render: (sav) =>
        sav.incidentDate ? (
          <span className="tw-text-sm tw-text-gray-500">
            {new Date(sav.incidentDate).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </span>
        ) : (
          <span className="tw-text-gray-400">-</span>
        ),
    },
    {
      key: 'amountSeller',
      label: 'Montant pris en charge',
      render: (sav) => {
        const total = (sav.amountSeller ?? 0) + (sav.amountSellerCommercial ?? 0);
        return (
          <span className="tw-text-sm tw-font-medium tw-text-green-600">
            {sav.amountSeller != null || sav.amountSellerCommercial != null ? formatAmount(total) : '-'}
          </span>
        );
      },
    },
  ], []);

  const filteredColumns = hideCompanyColumn ? columns.filter(c => c.key !== 'companyName') : columns;

  const statusBadges = (
    <div className="tw-flex tw-items-center tw-gap-3">
      {enCoursData?.total != null && (
        <span className="tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-bg-blue-100 tw-text-blue-700">
          {enCoursData.total} en cours
        </span>
      )}
      {terminesData?.total != null && (
        <span className="tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-bg-green-100 tw-text-green-700">
          {terminesData.total} terminé{terminesData.total > 1 ? 's' : ''}
        </span>
      )}
      {sansSuiteData?.total != null && (
        <span className="tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-bg-red-100 tw-text-red-700">
          {sansSuiteData.total} sans suite
        </span>
      )}
    </div>
  );

  return (
    <DataList
      embedded={embedded}
      data={savList}
      total={total}
      isLoading={isLoading}
      error={error as Error}
      title={hideTitle ? undefined : 'SAV'}
      titleIcon={hideTitle ? undefined : AlertTriangle}
      showCount={!hideTitle}
      headerActions={hideTitle ? undefined : customHeaderActions || statusBadges}
      columns={filteredColumns}
      filtersClassName="tw-mt-4"
      sortConfig={sortConfig}
      onSortChange={setSortConfig}
      filterSearch={
        <div className="tw-flex tw-flex-col tw-gap-1">
          <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Recherche</span>
          <FormInput
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(value) => setSearchTerm(value || '')}
            size="sm"
          />
        </div>
      }
      filters={
        <>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Statut</span>
            <FormSelect
              value={filters.status || ''}
              onChange={(value) => updateFilter('status', value || undefined)}
              options={SAV_STATUS_OPTIONS}
              size="sm"
            />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">État gestion</span>
            <FormSelect
              value={filters.virtualStatus || ''}
              onChange={(value) => updateFilter('virtualStatus', value || undefined)}
              options={SAV_GESTION_OPTIONS}
              size="sm"
            />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Date déclaration</span>
            <div className="tw-flex tw-gap-2">
              <FormInput
                type="date"
                value={filters.declarationDateFrom ?? ''}
                onChange={(value) => updateFilter('declarationDateFrom', value || undefined)}
                placeholder="Du"
                size="sm"
              />
              <FormInput
                type="date"
                value={filters.declarationDateTo ?? ''}
                onChange={(value) => updateFilter('declarationDateTo', value || undefined)}
                placeholder="Au"
                size="sm"
              />
            </div>
          </div>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Date de panne</span>
            <div className="tw-flex tw-gap-2">
              <FormInput
                type="date"
                value={filters.incidentDateFrom ?? ''}
                onChange={(value) => updateFilter('incidentDateFrom', value || undefined)}
                placeholder="Du"
                size="sm"
              />
              <FormInput
                type="date"
                value={filters.incidentDateTo ?? ''}
                onChange={(value) => updateFilter('incidentDateTo', value || undefined)}
                placeholder="Au"
                size="sm"
              />
            </div>
          </div>
        </>
      }
      activeFiltersCount={activeFiltersCount}
      onResetFilters={() => {
        resetFilters();
        setSearchTerm('');
      }}
      pagination={pagination}
      onPaginationChange={setPagination}
      getRowKey={(sav) => sav.id}
      onRowClick={
        onRowClick
          ? (sav) => {
              // Cloisonnement garant : seul le garant ouvre le détail (back
              // policy `guarantor-strict`). `canViewDetail === false` → ligne
              // inerte. `undefined` (listes internes) reste cliquable.
              if (sav.canViewDetail !== false) onRowClick(sav);
            }
          : undefined
      }
      rowClassName={(sav) =>
        [
          // Signature prime sur paiement si les 2 sont dus (rare).
          sav.needsGarantSignature
            ? 'tw-bg-red-50'
            : sav.needsGarantPayment
              ? 'tw-bg-amber-50'
              : '',
          sav.canViewDetail === false ? '!tw-cursor-default tw-text-gray-400' : '',
        ]
          .filter(Boolean)
          .join(' ')
      }
      mobileCardView={SavMobileCards}
      emptyMessage="Aucun SAV trouvé"
      emptyIcon={AlertTriangle}
    />
  );
}
